// Dashboard.tsx
import React, { useState, useEffect, useCallback } from "react";
import clsx from "clsx";
import axiosInstance from "../api/axiosInstance";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import { useSignalREvent } from '../hooks/useSignalREvent';

type TimeFrame =
  | "CurrentFY"
  | "LastFY"
  | "CurrentCY"
  | "LastCY"
  | "SinceInception"
  | "Custom";

interface KPI {
  title: string;
  value: number | string;
  currency?: string;
  trend: number;
  icon: React.ReactNode;
  color: string;
}

interface Order {
  id: string;
  table: string;
  items: number;
  amount: number;
  status: "Completed" | "Preparing" | "Served";
}

interface TopItem {
  name: string;
  orders: number;
  price: number;
  discountedPrice?: number;
  currency: string;
  image?: string;
}

// const COLORS = ["#FF9800", "#FFC107", "#FF5722"];
// Utility: generate color from string (type)
const stringToColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = Math.floor(
    Math.abs(Math.sin(hash) * 16777215) % 16777215
  ).toString(16);
  return `#${"000000".substring(0, 6 - color.length) + color}`;
};

// // Build absolute image URL
// const buildImageUrl = (path: string) => {
//   // const base = process.env.REACT_APP_API_BASE?.replace(/\/api\/?$/, "") || "";
//   // return `${base}/${String(path).replace(/^\/+/, "")}`;
//   return path; // already absolute
// };

const TIMEFRAMES: { value: TimeFrame; label: string }[] = [
  { value: "CurrentFY", label: "Current FY" },
  { value: "LastFY", label: "Last FY" },
  { value: "CurrentCY", label: "Current CY" },
  { value: "LastCY", label: "Last CY" },
  { value: "SinceInception", label: "Since Inception" },
  { value: "Custom", label: "Custom" },
];

const Dashboard: React.FC = () => {
  const [timeFrame, setTimeFrame] = useState<TimeFrame>("CurrentFY");
  const [customRange, setCustomRange] = useState<[Date | null, Date | null]>([null, null]);

  const [kpis, setKpis] = useState<KPI[]>([]);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState("");

  const [revenueTrend, setRevenueTrend] = useState<any[]>([]);
  const [revLoading, setRevLoading] = useState(false);
  const [revError, setRevError] = useState("");

  const [orderDistribution, setOrderDistribution] = useState<any[]>([]);
  const [orderDistLoading, setOrderDistLoading] = useState(false);
  const [orderDistError, setOrderDistError] = useState("");

  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [ordersError, setOrdersError] = useState("");

  const [topItems, setTopItems] = useState<TopItem[]>([]);
  const [topLoading, setTopLoading] = useState(false);
  const [topError, setTopError] = useState("");

  // Build query params for API
  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set("TimeFrame", timeFrame);
    if (timeFrame === "Custom" && customRange[0] && customRange[1]) {
      params.set("FromDate", customRange[0].toISOString().split("T")[0]);
      params.set("ToDate", customRange[1].toISOString().split("T")[0]);
    }
    return params.toString();
  }, [timeFrame, customRange]);

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async (params?: string) => {
    const finalParams = params || buildParams();

    // KPIs
    setKpiLoading(true); setKpiError("");
    try { const res = await axiosInstance.get(`/analytics/dashboard-kpis?${finalParams}`); setKpis(res.data || []); }
    catch (err: any) { setKpiError(err?.message || "Failed to load KPIs"); }
    finally { setKpiLoading(false); }

    // Revenue
    setRevLoading(true); setRevError("");
    try { const res = await axiosInstance.get(`/analytics/dashboard-RevenueTrend?${finalParams}`); setRevenueTrend(res.data || []); }
    catch (err: any) { setRevError(err?.response?.data?.message || err?.message || "Failed to load Revenue Trend"); }
    finally { setRevLoading(false); }

    // Order Distribution
    setOrderDistLoading(true); setOrderDistError("");
    try { const res = await axiosInstance.get(`/analytics/dashboard-OrderDistribution?${finalParams}`); setOrderDistribution(res.data || []); }
    catch (err: any) { setOrderDistError(err?.response?.data?.message || err?.message || "Failed to load Order Distribution"); }
    finally { setOrderDistLoading(false); }

    // Top Items
    setTopLoading(true); setTopError("");
    try {
      const res = await axiosInstance.get(`/analytics/dashboard-TopSellingItems?${finalParams}`);
      const mapped = (res.data || []).map((item: TopItem) => ({ ...item, image: item.image ? item.image : undefined }));
      setTopItems(mapped);
    } catch (err: any) { setTopError(err?.message || "Failed to load Top Items"); }
    finally { setTopLoading(false); }

  }, [buildParams]);

  // Only fetch for non-Custom time frames initially
  useEffect(() => {
    if (timeFrame !== "Custom") {
      fetchDashboardData();
    }
  }, [timeFrame, fetchDashboardData]);

  // Recent Orders (live)
  const fetchRecentOrders = useCallback(async () => {
    try { const res = await axiosInstance.get(`/analytics/dashboard-RecentOrders`); setRecentOrders(res.data || []); }
    catch (err: any) { setOrdersError(err?.message || "Failed to load Recent Orders"); }
  }, []);

  useEffect(() => {
    // Initial fetch
    fetchRecentOrders();
  }, [fetchRecentOrders]);

  // Subscribe to SignalR updates
  useSignalREvent( "LiveOrderUpdate","Dashboard", () => fetchRecentOrders());

  const onApplyCustom = () => {
    if (!customRange[0] || !customRange[1]) return;
    fetchDashboardData();
  };

  return (
    <section className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header / Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h2>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative">
            <select
              value={timeFrame}
              onChange={(e) => setTimeFrame(e.target.value as TimeFrame)}
              className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 py-2 pl-3 pr-8 rounded-lg text-sm shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {TIMEFRAMES.map((tf) => <option key={tf.value} value={tf.value}>{tf.label}</option>)}
            </select>
          </div>
          {timeFrame === "Custom" && (
            <div className="flex items-center gap-2">
              <DatePicker
                selected={customRange[0]}
                onChange={(dates: [Date | null, Date | null] | null) => { if (dates) setCustomRange(dates); }}
                startDate={customRange[0]}
                endDate={customRange[1]}
                selectsRange
                placeholderText="Select date range"
                className="border rounded-lg px-3 py-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm"
              />
              <button
                onClick={onApplyCustom}
                disabled={!customRange[0] || !customRange[1]}
                className={clsx(
                  "px-3 py-2 rounded-lg text-sm font-medium transition",
                  !customRange[0] || !customRange[1]
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                )}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Rest of Dashboard (KPIs, Charts, Orders, Top Items) */}
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
        {kpiLoading ? (
          <div className="col-span-full text-center text-gray-500">Loading KPIs...</div>
        ) : kpiError ? (
          <div className="col-span-full text-red-500">{kpiError}</div>
        ) : (
          kpis.map((kpi) => (
            <div
              key={kpi.title}
              className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow hover:shadow-md transition"
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-gray-700 dark:text-gray-200">{kpi.title}</div>
                <div
                  className="p-2 rounded-lg text-white"
                  style={{ backgroundColor: kpi.color }}
                >
                  {kpi.icon}
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {typeof kpi.value === "number"
                  ? `${kpi.currency || ""}${kpi.value.toLocaleString("en-IN")}`
                  : kpi.value}
              </div>
              {kpi.trend && <div
                className={`text-sm mt-1 ${kpi.trend >= 0 ? "text-green-500" : "text-red-500"
                  }`}
              >
                {kpi.trend >= 0 ? "+" : ""}
                {kpi.trend}% from last period
              </div>}
            </div>
          ))
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow hover:shadow-md transition chartContainer overflow-x-auto overflow-y-hidden">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">
              Revenue Trend
            </h4>
          </div>

          {revLoading ? (
            <div className="text-center text-gray-500">Loading...</div>
          ) : revError ? (
            <div className="text-red-500">{revError}</div>
          ) : (
            // Horizontal scroll wrapper
            <div
              className="w-full"
              style={{
                minWidth: `${revenueTrend.length * 60}px`, // each X-axis point gets 60px
              }}
            >
              <ResponsiveContainer width="100%" height={260}>
                <LineChart
                  data={revenueTrend}
                  margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                >
                  {/* gradient & glow defs */}
                  <defs>
                    <linearGradient id="revLine" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#06b6d4" stopOpacity={1} />
                      <stop offset="50%" stopColor="#3b82f6" stopOpacity={1} />
                      <stop offset="100%" stopColor="#8b5cf6" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="revArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.03} />
                    </linearGradient>
                    <filter id="revGlow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(17,24,39,0.06)" />

                  <XAxis
                    dataKey="xKey"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    angle={-30}
                    textAnchor="end"
                    interval={0}
                    height={50}
                  />

                  <YAxis
                    tickFormatter={(value: number) => {
                      const currency =
                        revenueTrend?.find((d) => d.currency)?.currency || "₹";
                      return `${currency} ${value.toLocaleString("en-IN")}`;
                    }}
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    width={80}
                  />

                  <RechartsTooltip
                    formatter={(value: number) => {
                      const currency =
                        revenueTrend?.find((d) => d.currency)?.currency || "₹";
                      return [`${currency} ${value.toLocaleString("en-IN")}`, "Revenue"];
                    }}
                    contentStyle={{
                      backgroundColor: "#0f172a",
                      color: "#f8fafc",
                      borderRadius: "0.5rem",
                      border: "none",
                      boxShadow: "0 6px 18px rgba(2,6,23,0.6)",
                    }}
                    labelStyle={{ color: "#f8fafc" }}
                  />

                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="none"
                    fill="url(#revArea)"
                    isAnimationActive={false}
                    tooltipType="none"
                  />

                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="url(#revLine)"
                    strokeWidth={3}
                    dot={false}
                    activeDot={
                      <circle
                        r={6}
                        fill="#fff"
                        stroke="#8b5cf6"
                        strokeWidth={3}
                        filter="url(#revGlow)"
                      />
                    }
                    animationDuration={1500}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <style>{`
    .chartContainer .recharts-cartesian-axis-tick-value {
      fill: #111827 !important;
    }
    .dark .chartContainer .recharts-cartesian-axis-tick-value {
      fill: #f9fafb !important;
    }
    /* invisible but scrollable */
    .chartContainer::-webkit-scrollbar {
      display: none;
    }
    .chartContainer {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
  `}</style>
        </div>

        {/* Order Distribution */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow hover:shadow-md transition">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Order Distribution
          </h4>
          {orderDistLoading ? (
            <div className="text-center text-gray-500">Loading...</div>
          ) : orderDistError ? (
            <div className="text-red-500">{orderDistError}</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={orderDistribution}
                  dataKey="value"
                  nameKey="type"
                  outerRadius={90}
                >
                  {orderDistribution.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={stringToColor(entry.type)} // unique per type
                    />
                  ))}
                </Pie>
                <Legend />
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Recent Orders & Top Items */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow hover:shadow-md transition">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100">
              Recent Orders
            </h4>
            <span className="flex items-center text-red-500 font-semibold animate-pulse">
              ● Live
            </span>
          </div>
          {ordersError ? (
            <div className="text-red-500">{ordersError}</div>
          ) : (
            <ul className="space-y-2">
              {recentOrders.map((order) => (
                <li
                  key={order.id}
                  className="flex justify-between items-center p-2 rounded-lg bg-gray-50 dark:bg-gray-700 transition"
                >
                  <div className="text-gray-900 dark:text-gray-100">
                    <span className="font-semibold">{order.id}</span> • {order.table} •{" "}
                    {order.items} items
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      ₹{order.amount}
                    </span>
                    <span
                      className={clsx(
                        "text-sm font-medium",
                        order.status === "Completed"
                          ? "text-green-500"
                          : order.status === "Preparing"
                            ? "text-yellow-500"
                            : "text-blue-500"
                      )}
                    >
                      {order.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top Items */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow hover:shadow-md transition">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Top Selling Items
          </h4>
          {topLoading ? (
            <div className="text-center text-gray-500">Loading...</div>
          ) : topError ? (
            <div className="text-red-500">{topError}</div>
          ) : (
            <ul className="space-y-2">
              {topItems.map((item) => (
                <li
                  key={item.name}
                  className="flex justify-between items-center p-2 rounded-lg bg-gray-50 dark:bg-gray-700 transition"
                >
                  <div className="flex items-center gap-2">
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-10 h-10 rounded-md object-cover"
                      />
                    )}
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-gray-100">
                        {item.name}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {item.orders} orders
                      </div>
                    </div>
                  </div>

                  {/* Price Section */}
                  <div className="flex items-center gap-2">
                    {item.discountedPrice ? (
                      <>
                        <span className="text-sm text-gray-500 dark:text-gray-400 line-through">
                          {item.currency}{item.price}
                        </span>
                        <span className="font-semibold text-green-600 dark:text-green-400">
                          {item.currency}{item.discountedPrice}
                        </span>
                      </>
                    ) : (
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {item.currency}{item.price}
                      </span>
                    )}
                  </div>

                </li>
              ))}
            </ul>
          )}
        </div>

      </div>
    </section>
  );
};

export default Dashboard;
